import { ref, computed, onMounted, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { isLoggedIn as readLoginHint } from '~/composables/usePostLoginRedirect';

/**
 * Reactive client-side login state for statically prerendered (SSG) pages.
 *
 * The public site is prerendered with no user context, so the baked HTML is
 * always logged-out and auth-aware UI must resolve on the client after hydration.
 *
 * The `is_logged_in` localStorage hint is the reliable persistent signal: it is
 * set on login and cleared on explicit logout (clearLoggedIn) and by the /lms
 * middleware when a session turns out to be stale. Auth0 is NOT reliable on these
 * pages — the client uses an in-memory cache, so on a full load of a public page
 * it has no session and reports isAuthenticated=false even for genuinely
 * logged-in users. We therefore let Auth0 only *add* to the hint (false -> true,
 * e.g. after an in-app login completes), never override it back to logged-out.
 *
 * Starts `false` to match the logged-out static HTML; nothing mutates during
 * setup, so hydration never mismatches (onMounted runs after hydration).
 */
export const useLoginState = () => {
   const loggedIn = ref(false);
   const { isAuthenticated } = useAuth0() ?? {};

   const resolveState = () => {
      loggedIn.value = readLoginHint() || !!isAuthenticated?.value;
   };

   // Post-hydration only. On the server/prerender neither hook runs, so the value
   // stays `false` and the emitted HTML matches the logged-out static output.
   onMounted(resolveState);

   // Catch Auth0 confirming a session mid-session (e.g. a login that completes on
   // an in-app navigation). Because resolveState ORs the hint, this can only turn
   // the state on, never off.
   watch(() => isAuthenticated?.value, resolveState);

   return {
      isLoggedIn: computed(() => loggedIn.value),
   };
};
